import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, AlertCircle, Loader2, Database, FileText,
  TrendingUp, Users, Mail, RefreshCw, Download, Send,
} from "lucide-react";

/**
 * Ops Console — single admin component covering:
 *   • Health diagnostics (Mongo, Resend, Stripe, JaaS, disk, schedulers)
 *   • Audit log viewer
 *   • Analytics (12 weeks of members / donations / messages / tier mix / top donors)
 *   • Lapsed-member finder + re-engagement send
 *   • Backups (manual run + list, nightly scheduler runs at 03:00 UTC)
 */
export default function OpsConsole() {
  const [tab, setTab] = useState("diag");
  const tabs = [
    { id: "diag", label: "Health", icon: Activity },
    { id: "insights", label: "Insights", icon: TrendingUp },
    { id: "audit", label: "Audit log", icon: FileText },
    { id: "lapsed", label: "Re-engage", icon: Users },
    { id: "backup", label: "Backups", icon: Database },
  ];
  return (
    <div className="space-y-6" data-testid="ops-console">
      <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-3">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`ops-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition-colors ${
                active ? "bg-navy text-pearl" : "bg-white text-navy border border-navy/15 hover:bg-navy/5"
              }`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "diag" && <DiagPanel />}
      {tab === "insights" && <InsightsPanel />}
      {tab === "audit" && <AuditPanel />}
      {tab === "lapsed" && <LapsedPanel />}
      {tab === "backup" && <BackupPanel />}
    </div>
  );
}

/* ─── HEALTH ─────────────────────────────────────────────────────────────── */
function DiagPanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(true);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/diag"); setD(r.data); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  useEffect(() => { refresh(); }, []);

  if (busy && !d) return <Skeleton />;
  if (!d) return null;
  const Pill = ({ ok, label, sub }) => (
    <div className={`rounded-sm p-4 border ${ok ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}>
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle2 size={16} className="text-green-700" /> : <AlertCircle size={16} className="text-red-700" />}
        <div className="text-sm font-semibold text-navy">{label}</div>
      </div>
      {sub && <div className="text-xs text-slate-600 mt-1">{sub}</div>}
    </div>
  );

  return (
    <section data-testid="ops-diag">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-display text-xl text-navy">System health</h3>
        <span className={`text-xs px-2 py-1 rounded-sm ${d.overall_ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
          {d.overall_ok ? "All systems operational" : "Attention required"}
        </span>
        <button onClick={refresh} className="ml-auto text-xs text-slate-500 hover:text-navy inline-flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Pill ok={d.mongo?.ok} label="MongoDB" sub={d.mongo?.ok ? `${d.mongo.users} users · ${d.mongo.events} events` : d.mongo?.error} />
        <Pill ok={d.resend?.ok} label="Resend (email)" sub={d.resend?.verified_domains?.join(", ") || d.resend?.note || d.resend?.error} />
        <Pill ok={d.stripe_configured} label="Stripe" sub={d.stripe_configured ? "API key configured" : "Not configured"} />
        <Pill ok={d.jaas_configured} label="JaaS (video)" sub={d.jaas_configured ? "Credentials present" : "Missing keys"} />
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <KeyValueBox title="Last activity" rows={[
          ["Trustee digest", d.last_trustee_digest?.at ? new Date(d.last_trustee_digest.at).toLocaleString() : "—"],
          ["Newsletter send", d.last_newsletter_send?.at ? new Date(d.last_newsletter_send.at).toLocaleString() : "—"],
          ["Governance send", d.last_governance_send?.at ? new Date(d.last_governance_send.at).toLocaleString() : "—"],
          ["Payment", d.last_payment?.at ? new Date(d.last_payment.at).toLocaleString() : "—"],
          ["Audit entry", d.last_audit_entry?.at ? new Date(d.last_audit_entry.at).toLocaleString() : "—"],
          ["Backup", d.last_backup || "—"],
        ]} />
        <KeyValueBox title="Disk usage" rows={[
          ["Total", `${d.disk?.total_gb || 0} GB`],
          ["Used", `${d.disk?.used_gb || 0} GB (${d.disk?.percent_used || 0}%)`],
          ["Free", `${d.disk?.free_gb || 0} GB`],
          ["Gemini key", d.gemini_configured ? "Configured" : "Missing"],
        ]} />
      </div>
      <p className="mt-4 text-xs text-slate-500 italic">
        Checked: {new Date(d.checked_at).toLocaleString()}
      </p>
    </section>
  );
}

/* ─── INSIGHTS ──────────────────────────────────────────────────────────── */
function InsightsPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/insights").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Skeleton />;
  const maxM = Math.max(1, ...d.weeks.map((w) => w.members));
  const maxD = Math.max(1, ...d.weeks.map((w) => w.donations_gbp));
  return (
    <section className="space-y-6" data-testid="ops-insights">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Chip label="Total members" value={d.totals.total_members} />
        <Chip label="Active (30d)" value={d.totals.active_30d} color="#5d8259" />
        <Chip label="Lapsed (60d+)" value={d.totals.lapsed_60d_plus} color="#E07A5F" />
        <Chip label="Donations" value={d.totals.total_donations_count} />
        <Chip label="Messages" value={d.totals.total_messages} />
      </div>

      <div>
        <h4 className="font-display text-lg text-navy mb-3">Members joining (last 12 weeks)</h4>
        <div className="flex items-end gap-1 h-32">
          {d.weeks.map((w, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end">
              <div className="w-full bg-saffron rounded-t" style={{ height: `${(w.members / maxM) * 100}%`, minHeight: w.members > 0 ? 4 : 0 }} title={`${w.members} members`} />
              <div className="text-[9px] text-slate-500 mt-1">{w.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-display text-lg text-navy mb-3">Donations £ (last 12 weeks)</h4>
        <div className="flex items-end gap-1 h-32">
          {d.weeks.map((w, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end">
              <div className="w-full bg-green-600 rounded-t" style={{ height: `${(w.donations_gbp / maxD) * 100}%`, minHeight: w.donations_gbp > 0 ? 4 : 0 }} title={`£${w.donations_gbp}`} />
              <div className="text-[9px] text-slate-500 mt-1">£{w.donations_gbp.toFixed(0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <h4 className="font-display text-lg text-navy mb-3">Member tier mix</h4>
          <div className="space-y-2">
            {d.tiers.map((t) => (
              <div key={t.tier} className="flex items-center gap-3">
                <span className="text-sm w-28 text-slate-700">{t.tier}</span>
                <div className="flex-1 h-3 bg-pearl rounded-sm overflow-hidden">
                  <div className="h-full bg-navy" style={{ width: `${(t.count / d.totals.total_members) * 100}%` }} />
                </div>
                <span className="text-xs text-slate-500 w-8 text-right">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-display text-lg text-navy mb-3">Top supporters</h4>
          <table className="w-full text-sm">
            <tbody>
              {d.top_donors.map((dn, i) => (
                <tr key={i} className="border-b border-navy/5">
                  <td className="py-1.5 pr-2 text-slate-700">{dn.name}</td>
                  <td className="py-1.5 pr-2 text-xs text-slate-400">{dn.email_hash}</td>
                  <td className="py-1.5 text-right text-navy font-medium">£{dn.total_gbp.toFixed(0)}</td>
                </tr>
              ))}
              {d.top_donors.length === 0 && <tr><td className="py-2 italic text-slate-500">No donations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ─── AUDIT ─────────────────────────────────────────────────────────────── */
function AuditPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/admin/audit").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  const visible = filter
    ? items.filter((i) => (i.action + (i.actor || "") + JSON.stringify(i.details || {})).toLowerCase().includes(filter.toLowerCase()))
    : items;
  return (
    <section data-testid="ops-audit">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-display text-xl text-navy">Audit log</h3>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by action / actor"
          data-testid="audit-filter"
          className="ml-auto text-xs border border-navy/15 px-3 py-1.5 rounded-sm w-64"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left border-b border-navy/10 text-[10px] uppercase tracking-widest text-slate-500">
              <th className="py-2 pr-3">When</th>
              <th className="py-2 pr-3">Actor</th>
              <th className="py-2 pr-3">Action</th>
              <th className="py-2 pr-3">Target</th>
              <th className="py-2 pr-3">Details</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((i) => (
              <tr key={i.audit_id} className="border-b border-navy/5 align-top">
                <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{new Date(i.at).toLocaleString()}</td>
                <td className="py-2 pr-3 text-navy">{i.actor}</td>
                <td className="py-2 pr-3 font-mono text-[11px] text-saffron">{i.action}</td>
                <td className="py-2 pr-3 text-slate-600">{i.target || "—"}</td>
                <td className="py-2 pr-3 text-slate-500 font-mono text-[10px] max-w-md truncate">{JSON.stringify(i.details)}</td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={5} className="py-3 italic text-slate-500">No matching audit entries.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ─── LAPSED / RE-ENGAGE ────────────────────────────────────────────────── */
function LapsedPanel() {
  const [days, setDays] = useState(60);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);

  const load = async () => {
    const r = await api.get(`/admin/lapsed-members?days=${days}`);
    setItems(r.data.items || []);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, [days]);

  const toggle = (uid) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(uid) ? n.delete(uid) : n.add(uid);
      return n;
    });
  };
  const all = () => setSelected(new Set(items.map((i) => i.user_id)));
  const none = () => setSelected(new Set());

  const send = async () => {
    if (selected.size === 0) return toast.error("Select at least one member.");
    if (!confirm(`Send re-engagement email to ${selected.size} member(s)?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/re-engage", { user_ids: [...selected] });
      toast.success(`Sent ${r.data.sent} email(s); ${r.data.skipped_count} skipped.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSending(false);
  };

  return (
    <section data-testid="ops-lapsed">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-display text-xl text-navy">Lapsed members</h3>
        <label className="text-xs text-slate-600">
          No login for
          <input type="number" min={14} max={365} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 60)} className="ml-2 w-16 border border-navy/15 px-2 py-1 rounded-sm text-xs" />
          days
        </label>
        <span className="text-xs text-slate-500">{items.length} flagged · {selected.size} selected</span>
        <div className="ml-auto flex gap-2">
          <button onClick={all} className="text-xs text-navy underline">Select all</button>
          <button onClick={none} className="text-xs text-slate-500 underline">Clear</button>
          <button
            onClick={send}
            disabled={sending || selected.size === 0}
            data-testid="lapsed-send-btn"
            className="px-3 py-1.5 bg-saffron text-navy rounded-sm text-xs font-medium hover:bg-saffron-light disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Re-engage selected
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-navy/10 text-[10px] uppercase tracking-widest text-slate-500">
              <th className="py-2 pr-3 w-8"></th>
              <th className="py-2 pr-3">Member</th>
              <th className="py-2 pr-3">Tier</th>
              <th className="py-2 pr-3">Joined</th>
              <th className="py-2 pr-3">Last login</th>
              <th className="py-2 pr-3">Last re-engaged</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.user_id} className="border-b border-navy/5">
                <td><input type="checkbox" checked={selected.has(m.user_id)} onChange={() => toggle(m.user_id)} /></td>
                <td className="py-2 pr-3 text-navy">{m.name || m.email}<div className="text-[10px] text-slate-400">{m.email}</div></td>
                <td className="py-2 pr-3 text-xs">{m.tier || "Standard"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">{(m.created_at || "").slice(0, 10)}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">{m.last_login_at ? new Date(m.last_login_at).toLocaleDateString() : "never"}</td>
                <td className="py-2 pr-3 text-xs text-slate-400">{m.re_engagement_sent_at ? new Date(m.re_engagement_sent_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="py-3 italic text-slate-500">No lapsed members 🎉</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ─── BACKUPS ──────────────────────────────────────────────────────────── */
function BackupPanel() {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const load = async () => {
    const r = await api.get("/admin/backup/list");
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    if (!confirm("Run a MongoDB snapshot now?")) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/backup/run");
      if (r.data.ok) toast.success(`Backup saved: ${r.data.file} (${Math.round(r.data.size_bytes / 1024)} KB)`);
      else toast.error(r.data.error || "Backup failed");
      load();
    } catch (e) { toast.error(e.message); }
    setRunning(false);
  };
  return (
    <section data-testid="ops-backups">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-display text-xl text-navy">Database backups</h3>
        <span className="text-xs text-slate-500">Nightly @ 03:00 UTC · keeps last 30</span>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="backup-run-btn"
          className="ml-auto px-3 py-1.5 bg-navy text-pearl rounded-sm text-xs font-medium hover:bg-navy-light disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
          Run snapshot now
        </button>
      </div>
      <table className="w-full text-sm" data-testid="backup-list">
        <thead>
          <tr className="text-left border-b border-navy/10 text-[10px] uppercase tracking-widest text-slate-500">
            <th className="py-2 pr-3">File</th>
            <th className="py-2 pr-3">Size</th>
            <th className="py-2 pr-3">Created</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.file} className="border-b border-navy/5">
              <td className="py-2 pr-3 font-mono text-xs">{i.file}</td>
              <td className="py-2 pr-3 text-xs">{i.size_mb} MB</td>
              <td className="py-2 pr-3 text-xs text-slate-500">{new Date(i.modified).toLocaleString()}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={3} className="py-3 italic text-slate-500">No backups yet. Run one now.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */
function Chip({ label, value, color = "#0B1F4D" }) {
  return (
    <div className="bg-white border border-navy/10 rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color }}>{label}</div>
      <div className="font-display text-3xl text-navy mt-1">{value}</div>
    </div>
  );
}
function KeyValueBox({ title, rows }) {
  return (
    <div className="bg-white border border-navy/10 rounded-sm p-5">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">{title}</div>
      <dl className="space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 text-sm">
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-navy font-medium text-right">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
function Skeleton() {
  return <div className="h-40 bg-pearl/50 animate-pulse rounded-sm" />;
}
